/**
 * AirlinesAdmin - Super Admin page for managing airlines
 *
 * Features:
 * - View all airlines with statistics
 * - Create new airline
 * - Edit airline details (name, code, region, settings)
 * - Delete airline with confirmation
 * - View user count, document count, fleet count per airline
 *
 * Permissions: Super Admin only (Global airline admin)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import { Airline } from '../../lib/types';
import {
  Plus,
  Edit,
  Trash2,
  Building2,
  Users,
  FileText,
  Truck,
  X,
} from 'lucide-react';
import { COCKPIT_LANGUAGE_OPTIONS } from '../../lib/constants';

// =====================================================================
// Types
// =====================================================================

interface AirlineWithStats extends Airline {
  user_count: number;
  doc_count: number;
  fleet_count: number;
}

interface AirlineFormData {
  name: string;
  code: string;
  region: string;
  language_code: string;
  expected_languages: string[];
  settings?: {
    iata?: string;
    icao?: string;
    description?: string;
  };
}

// =====================================================================
// Validation Schema
// =====================================================================

const airlineSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  code: z.string().min(2, 'Code must be at least 2 characters').max(10, 'Code too long'),
  region: z.string().min(2, 'Region must be at least 2 characters'),
  language_code: z.string().min(1),
  expected_languages: z.array(z.string()).optional(),
  iata: z.string().optional(),
  icao: z.string().optional(),
  description: z.string().optional(),
});

/** Languages available for expected_languages multi-select (always includes English) */
const EXPECTED_LANGUAGE_CHOICES = COCKPIT_LANGUAGE_OPTIONS
  .filter((opt) => opt.code !== null)
  .map((opt) => ({ code: opt.code!, label: opt.label }));

type AirlineSchemaType = z.infer<typeof airlineSchema>;

// =====================================================================
// Main Component
// =====================================================================

export default function AirlinesAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAirline, setEditingAirline] = useState<AirlineWithStats | null>(null);
  const [deletingAirline, setDeletingAirline] = useState<AirlineWithStats | null>(null);

  // ===================================================================
  // Fetch Airlines with Stats
  // ===================================================================

  const { data: airlines, isLoading } = useQuery<AirlineWithStats[]>({
    queryKey: ['airlines-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('airlines')
        .select(`
          *,
          user_airline_memberships(count),
          doc_library(count),
          airline_fleets(count)
        `)
        .order('name');

      if (error) throw error;

      return (data || []).map((airline: any) => ({
        ...airline,
        user_count: airline.user_airline_memberships?.[0]?.count || 0,
        doc_count: airline.doc_library?.[0]?.count || 0,
        fleet_count: airline.airline_fleets?.[0]?.count || 0,
      }));
    },
    enabled: isSuperAdmin,
  });

  // ===================================================================
  // Mutations
  // ===================================================================

  const createMutation = useMutation({
    mutationFn: async (formData: AirlineFormData) => {
      const { data, error } = await supabase
        .from('airlines')
        .insert({
          name: formData.name,
          code: formData.code,
          region: formData.region,
          language_code: formData.language_code || 'en',
          expected_languages: formData.expected_languages.length > 0 ? formData.expected_languages : null,
          settings: formData.settings || {},
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airlines-admin'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: AirlineFormData }) => {
      const { data, error } = await supabase
        .from('airlines')
        .update({
          name: formData.name,
          code: formData.code,
          region: formData.region,
          language_code: formData.language_code || 'en',
          expected_languages: formData.expected_languages.length > 0 ? formData.expected_languages : null,
          settings: formData.settings || {},
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airlines-admin'] });
      setEditingAirline(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Check if Global airline
      if (id === '00000000-0000-0000-0000-000000000000') {
        throw new Error('Cannot delete Global airline');
      }

      const { error } = await supabase
        .from('airlines')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['airlines-admin'] });
      setDeletingAirline(null);
    },
  });

  // ===================================================================
  // Permission Check
  // ===================================================================

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Airlines Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage all airlines, view statistics, and configure settings
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add Airline
        </button>
      </div>

      {/* Airlines Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading airlines...</div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Airline
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Region
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Lang
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Cockpit Languages
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  <Users className="w-4 h-4 inline" />
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  <FileText className="w-4 h-4 inline" />
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  <Truck className="w-4 h-4 inline" />
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {airlines?.map((airline) => (
                <tr key={airline.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-gray-900 flex items-center gap-1">
                          <span className="truncate">{airline.name}</span>
                          {airline.id === '00000000-0000-0000-0000-000000000000' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800 flex-shrink-0">
                              Global
                            </span>
                          )}
                        </div>
                        {airline.settings?.description && (
                          <div className="text-xs text-gray-500 truncate">{airline.settings.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-xs text-gray-900">{airline.code}</div>
                    {(airline.settings?.iata || airline.settings?.icao) && (
                      <div className="text-xs text-gray-500">
                        {airline.settings.iata && airline.settings.iata}
                        {airline.settings.iata && airline.settings.icao && '/'}
                        {airline.settings.icao && airline.settings.icao}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {airline.region}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {airline.language_code || 'en'}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {(airline as any).expected_languages
                      ? (airline as any).expected_languages.join(', ')
                      : <span className="text-gray-400">auto</span>}
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {airline.user_count}
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {airline.doc_count}
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {airline.fleet_count}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingAirline(airline)}
                        className="p-1 text-sky-600 hover:bg-sky-50 rounded"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeletingAirline(airline)}
                        disabled={airline.id === '00000000-0000-0000-0000-000000000000'}
                        className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <AirlineFormModal
          title="Create New Airline"
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          error={createMutation.error as Error | null}
        />
      )}

      {/* Edit Modal */}
      {editingAirline && (
        <AirlineFormModal
          title="Edit Airline"
          airline={editingAirline}
          onClose={() => setEditingAirline(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editingAirline.id, formData: data })}
          isLoading={updateMutation.isPending}
          error={updateMutation.error as Error | null}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingAirline && (
        <DeleteConfirmationModal
          airline={deletingAirline}
          onClose={() => setDeletingAirline(null)}
          onConfirm={() => deleteMutation.mutate(deletingAirline.id)}
          isLoading={deleteMutation.isPending}
          error={deleteMutation.error as Error | null}
        />
      )}
    </div>
  );
}

// =====================================================================
// Airline Form Modal
// =====================================================================

interface AirlineFormModalProps {
  title: string;
  airline?: AirlineWithStats;
  onClose: () => void;
  onSubmit: (data: AirlineFormData) => void;
  isLoading: boolean;
  error: Error | null;
}

const AirlineFormModal: React.FC<AirlineFormModalProps> = ({
  title,
  airline,
  onClose,
  onSubmit,
  isLoading,
  error,
}) => {
  const [selectedLangs, setSelectedLangs] = useState<string[]>(
    (airline as any)?.expected_languages || [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AirlineSchemaType>({
    resolver: zodResolver(airlineSchema),
    defaultValues: {
      name: airline?.name || '',
      code: airline?.code || '',
      region: airline?.region || '',
      language_code: airline?.language_code || 'en',
      iata: airline?.settings?.iata || '',
      icao: airline?.settings?.icao || '',
      description: airline?.settings?.description || '',
    },
  });

  const toggleLang = (code: string) => {
    setSelectedLangs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleFormSubmit = (data: AirlineSchemaType) => {
    // Ensure 'en' is always included if any languages are selected
    const langs = selectedLangs.length > 0 && !selectedLangs.includes('en')
      ? ['en', ...selectedLangs]
      : selectedLangs;

    const formData: AirlineFormData = {
      name: data.name,
      code: data.code,
      region: data.region,
      language_code: data.language_code,
      expected_languages: langs,
      settings: {
        iata: data.iata,
        icao: data.icao,
        description: data.description,
      },
    };
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="SN Brussels Airlines"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('code')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="SNB"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            {/* Region */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('region')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="EU"
              />
              {errors.region && (
                <p className="mt-1 text-sm text-red-600">{errors.region.message}</p>
              )}
            </div>

            {/* Language Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Language
              </label>
              <select
                {...register('language_code')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
              >
                <option value="en">English (default)</option>
                {COCKPIT_LANGUAGE_OPTIONS.filter((opt) => opt.code !== null).map((opt) => (
                  <option key={opt.code} value={opt.code!}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Org-level language for transcription. Non-native languages trigger two-pass pipeline.
              </p>
            </div>

            {/* Expected Languages (multi-select) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cockpit Languages
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Languages crews at this airline typically speak. Used for code-switching detection (max 3-4 recommended).
                English is always included. Leave empty to auto-detect from Default Language.
              </p>
              <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                {EXPECTED_LANGUAGE_CHOICES.map((lang) => (
                  <label
                    key={lang.code}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                      selectedLangs.includes(lang.code)
                        ? 'bg-sky-50 text-sky-800 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLangs.includes(lang.code)}
                      onChange={() => toggleLang(lang.code)}
                      className="rounded border-gray-300 text-sky-600 focus:ring-sky-500 h-3.5 w-3.5"
                    />
                    {lang.label}
                  </label>
                ))}
              </div>
              {selectedLangs.length > 0 && (
                <p className="mt-1 text-xs text-sky-600">
                  Selected: en, {selectedLangs.filter((c) => c !== 'en').join(', ')}
                  {selectedLangs.length > 4 && (
                    <span className="text-amber-600 ml-1">(more than 4 may reduce detection accuracy)</span>
                  )}
                </p>
              )}
            </div>

            {/* IATA Code (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IATA Code
              </label>
              <input
                type="text"
                {...register('iata')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="SN"
              />
            </div>

            {/* ICAO Code (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ICAO Code
              </label>
              <input
                type="text"
                {...register('icao')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="BEL"
              />
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="Belgian flag carrier airline"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : airline ? 'Update Airline' : 'Create Airline'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Delete Confirmation Modal
// =====================================================================

interface DeleteConfirmationModalProps {
  airline: AirlineWithStats;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: Error | null;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  airline,
  onClose,
  onConfirm,
  isLoading,
  error,
}) => {
  const hasResources = airline.user_count > 0 || airline.doc_count > 0 || airline.fleet_count > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Airline?</h2>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error.message}</p>
          </div>
        )}

        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{airline.name}</strong>?
        </p>

        {hasResources && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p className="font-medium">Warning</p>
            <p className="text-sm">
              This airline has {airline.user_count} user(s), {airline.doc_count} document(s), and{' '}
              {airline.fleet_count} fleet(s). Deleting will cascade to all related records.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading ? 'Deleting...' : 'Delete Airline'}
          </button>
        </div>
      </div>
    </div>
  );
};
